import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Bell,
  ChevronDown,
  CircleUserRound,
  Menu,
  X,
  ShieldAlert,
  User,
  Settings,
} from "lucide-react";

// percorsi
const SETUP_EDIT_PATH = "/setup/edit";
const ADMIN_PATH = "/admin";
const NOTIFICHE_PATH = "/notifiche";
const PANIC_PATH = "/panic";

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handler(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onOutside?.();
    }
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onOutside]);
}

export default function TopBar({
  title = "NORMA HACCP",
  showBack = false,
  user = { name: "admin", email: "" },
  onLogout = null,
  onPanic = null,
  quickActions = [],
  menus = [],
  notificationsCount = 0,
  isSuperadmin = false,
}) {
  const navigate = useNavigate();

  const [openUser, setOpenUser] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);

  const userRef = useRef(null);
  const mobileRef = useRef(null);

  useClickOutside(userRef, () => setOpenUser(false));
  useClickOutside(mobileRef, () => setOpenMobile(false));

  const go = (path) => {
    try {
      navigate(path);
    } catch {
      window.location.href = path;
    }
  };

  const handleLogout = async () => {
    setOpenUser(false);
    setOpenMobile(false);

    if (typeof onLogout === "function") {
      await onLogout();
      go("/login");
      return;
    }

    try {
      await fetch("/api/logout.php", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    } finally {
      go("/login");
    }
  };

  const handlePanic = () => {
    if (!isSuperadmin) return;

    setOpenUser(false);
    setOpenMobile(false);

    if (typeof onPanic === "function") {
      onPanic();
      return;
    }

    go(PANIC_PATH);
  };

  const iconBtn =
    "relative inline-flex items-center justify-center rounded-full w-10 h-10 " +
    "bg-[#E8EEF4] text-[#043662] hover:bg-[#dfe6ee] transition";

  const pillBtn =
    "inline-flex items-center gap-2 rounded-full h-10 px-4 " +
    "bg-[#043662] text-white hover:bg-[#032f56] transition shadow-sm";

  const panicBtnEnabled =
    "relative h-10 px-5 rounded-full text-white inline-flex items-center gap-2 " +
    "bg-gradient-to-r from-[#FF5B77] to-[#F43F5E] shadow-sm";

  const panicBtnDisabled =
    "relative h-10 px-5 rounded-full text-white inline-flex items-center gap-2 " +
    "bg-gradient-to-r from-[#FF5B77] to-[#F43F5E] shadow-sm opacity-70 cursor-not-allowed";

  const panicMobileEnabled =
    "relative w-full py-2.5 px-3 rounded-xl text-white flex items-center gap-2 " +
    "bg-gradient-to-r from-[#FF5B77] to-[#F43F5E]";

  const panicMobileDisabled =
    "relative w-full py-2.5 px-3 rounded-xl text-white flex items-center gap-2 " +
    "bg-gradient-to-r from-[#FF5B77] to-[#F43F5E] opacity-70 cursor-not-allowed";

  return (
    <div className="sticky top-0 z-40 w-full bg-white border-b border-slate-200">
      <div className="mx-auto max-w-screen-2xl px-3 sm:px-4">
        <div className="h-16 flex items-center gap-3 relative">
          <div
            className="flex items-center gap-3 cursor-pointer select-none min-w-0 shrink"
            onClick={() => go("/")}
          >
            <img
              src="/logonorma.png"
              alt="Norma"
              className="h-10 w-auto object-contain shrink-0"
            />
            <div className="hidden sm:flex flex-col leading-tight min-w-0">
              <span className="text-[#003B71] text-xl font-bold truncate">
                Norma
              </span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                Food Safety Pilot
              </span>
            </div>
          </div>

          <div className="hidden lg:block min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {title}
            </div>
          </div>

          <div className="ml-auto hidden md:flex items-center gap-3">
            <button
              onClick={() => go(SETUP_EDIT_PATH)}
              className="inline-flex items-center justify-center rounded-full w-10 h-10 bg-[#E8EEF4] text-[#043662] hover:bg-[#dfe6ee] transition"
              title="Autoconfiguratore"
            >
              <Settings size={18} />
            </button>

            <button
              onClick={handlePanic}
              disabled={!isSuperadmin}
              className={isSuperadmin ? panicBtnEnabled : panicBtnDisabled}
              title={isSuperadmin ? "Apri Panic Mode" : "Non incluso nel piano"}
            >
              <ShieldAlert size={18} />
              Panic Mode

              {!isSuperadmin && (
                <span className="absolute -top-2 -right-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-white/20 text-white border border-white/30 backdrop-blur-sm">
                  Upgrade
                </span>
              )}
            </button>

            <button
              className={iconBtn}
              onClick={() => go(NOTIFICHE_PATH)}
              aria-label="Notifiche"
            >
              <Bell size={18} />
              {notificationsCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {notificationsCount > 99 ? "99+" : notificationsCount}
                </span>
              )}
            </button>

            <button onClick={() => go(ADMIN_PATH)} className={pillBtn}>
              <User size={16} />
              Admin
            </button>

            <div className="relative" ref={userRef}>
              <button
                onClick={() => setOpenUser((v) => !v)}
                className={pillBtn}
              >
                <CircleUserRound size={18} />
                <span className="max-w-[140px] truncate">
                  {user?.name || "Utente"}
                </span>
                <ChevronDown size={16} />
              </button>

              {openUser && (
                <div className="absolute right-0 mt-2 w-56 bg-white shadow rounded-xl border overflow-hidden">
                  <div className="px-4 py-3 border-b bg-slate-50">
                    <div className="font-medium text-slate-800">
                      {user?.name || "Utente"}
                    </div>
                    {user?.email ? (
                      <div className="text-sm text-slate-500 truncate">
                        {user.email}
                      </div>
                    ) : null}
                  </div>

                  <button
                    onClick={() => {
                      setOpenUser(false);
                      go(SETUP_EDIT_PATH);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Settings size={16} />
                    Autoconfiguratore
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-2 text-red-600"
                  >
                    <LogOut size={16} />
                    Esci
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="ml-auto flex md:hidden items-center gap-2" ref={mobileRef}>
            <button
              onClick={handlePanic}
              disabled={!isSuperadmin}
              className={isSuperadmin ? panicMobileEnabled : panicMobileDisabled}
              title={isSuperadmin ? "Apri Panic Mode" : "Non incluso nel piano"}
            >
              <ShieldAlert size={16} />
              <span className="text-sm">Panic</span>

              {!isSuperadmin && (
                <span className="absolute -top-2 -right-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-white/20 text-white border border-white/30 backdrop-blur-sm">
                  Upgrade
                </span>
              )}
            </button>

            <button
              onClick={() => setOpenMobile((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100"
              aria-label="Menu"
            >
              {openMobile ? <X size={22} /> : <Menu size={22} />}
            </button>

            {openMobile && (
              <div className="absolute right-0 top-16 w-72 bg-white shadow-xl rounded-2xl border overflow-hidden">
                <div className="p-3 space-y-2">
                  <div className="px-1 pb-2 border-b">
                    <div className="font-semibold text-slate-800">
                      {user?.name || "Utente"}
                    </div>
                    {user?.email ? (
                      <div className="text-sm text-slate-500 truncate">
                        {user.email}
                      </div>
                    ) : null}
                  </div>

                  <button
                    onClick={() => {
                      setOpenMobile(false);
                      go(SETUP_EDIT_PATH);
                    }}
                    className="w-full py-2.5 px-3 bg-[#E8EEF4] text-[#043662] rounded-xl flex items-center justify-center"
                    title="Autoconfiguratore"
                  >
                    <Settings size={18} />
                  </button>

                  <button
                    onClick={() => {
                      setOpenMobile(false);
                      go(ADMIN_PATH);
                    }}
                    className="w-full py-2.5 px-3 bg-[#043662] text-white rounded-xl flex items-center gap-2"
                  >
                    <User size={16} />
                    Admin
                  </button>

                  <button
                    onClick={() => {
                      setOpenMobile(false);
                      go(NOTIFICHE_PATH);
                    }}
                    className="w-full py-2.5 px-3 border rounded-xl flex items-center gap-2"
                  >
                    <Bell size={16} />
                    Notifiche
                    {notificationsCount > 0 && (
                      <span className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                        {notificationsCount > 99 ? "99+" : notificationsCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      if (!isSuperadmin) return;
                      setOpenMobile(false);
                      handlePanic();
                    }}
                    disabled={!isSuperadmin}
                    className={isSuperadmin ? panicMobileEnabled : panicMobileDisabled}
                  >
                    <ShieldAlert size={16} />
                    Panic Mode
                    {!isSuperadmin && (
                      <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-white/20 text-white border border-white/30">
                        Upgrade
                      </span>
                    )}
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full py-2.5 px-3 border rounded-xl text-red-600 flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    Esci
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
